// ENG-8093: a non-secret fingerprint of the PROVIDER credentials an opencode
// serve was spawned with, so a rotation can be detected and acted on.
//
// `ensureOpencodeRuntime` rebuilds `serveEnv` on every poll, but `serveEnv` only
// takes effect at spawn — the launcher bakes it into the tmux process env — and
// the function early-returns while the serve is healthy. So a live serve holds
// whatever credential it started with, indefinitely. A rotated or expired key
// then 401s on EVERY turn while the manager reports the agent perfectly healthy,
// because the serve process is up and only the model call fails.
//
// That window got longer, not shorter, with ENG-7995: fewer accidental restarts
// means a stale key survives longer.
//
// This mirrors the Claude Code auth-tuple (`claudeAuthTupleBySession`), which
// solves the same problem for the other runtime — and deliberately stays a
// FINGERPRINT rather than the value, because the comparison result is logged and
// `manager.log` is persisted to disk.
//
// ## Why the same hash the server uses
//
// /host/refresh already ships `openrouter.fingerprint`, computed server-side as
// `sha256(auth_token).slice(0, 16)`. We prefer it when present but fall back to
// computing the identical hash locally, so the fingerprint of a given token is
// the SAME value either way. Without that, an API upgrade that started (or
// stopped) sending the field would look like a rotation and respawn every
// opencode serve in the fleet for no reason.
//
// ## What is deliberately NOT in the set
//
//   - `AGT_API_KEY`. It is a credential in the serve env, and rotating it does
//     strand a live serve — but it is HOST-wide, so including it would respawn
//     every agent on the host simultaneously on one `agt host rotate-key`. That
//     is a different blast radius than a per-agent provider rotation and wants
//     its own decision; ENG-8093 is scoped to provider credentials.
//   - `XAI_API_KEY` and any other operator-set host-level key. These never appear
//     in `serveEnv` at all — they reach the serve through the launcher's
//     `process.env` forwarding — so the manager has no delivered value to
//     compare, and a change to them already implies a manager restart.

import { createHash } from 'node:crypto';

/** The server's fingerprint width (host-runtime.ts). Matching it keeps the two interchangeable. */
const FINGERPRINT_LENGTH = 16;

/** Marker for "this provider had no credential", distinct from any real hash. */
const ABSENT = 'none';

export interface OpencodeCredentialInputs {
  /** `refreshData.openrouter.auth_token` — the per-agent OpenRouter key. */
  openRouterToken?: string | null;
  /** `refreshData.openrouter.fingerprint` — server-computed, preferred when present. */
  openRouterFingerprint?: string | null;
  /**
   * `refreshData.anthropic.api_key` — the host-level direct-Anthropic key
   * (ENG-7993). The refresh payload carries no fingerprint for this one, so it is
   * always hashed locally.
   */
  anthropicKey?: string | null;
}

function fingerprintOf(secret: string | null | undefined): string {
  if (!secret) return ABSENT;
  return createHash('sha256').update(secret, 'utf8').digest('hex').slice(0, FINGERPRINT_LENGTH);
}

/**
 * A stable, non-secret identifier for the provider-credential set.
 *
 * Safe to log and to compare. Never contains, and cannot be reversed to, a raw
 * key. Absent credentials are represented explicitly so that ADDING a credential
 * (keyless → keyed) registers as a change rather than looking like no-op.
 */
export function opencodeCredentialFingerprint(inputs: OpencodeCredentialInputs): string {
  // Prefer the server's value, but only when there is actually a token it could
  // describe — a fingerprint with no token would claim a credential the serve was
  // never given.
  const openRouter = inputs.openRouterToken
    ? (inputs.openRouterFingerprint || fingerprintOf(inputs.openRouterToken))
    : ABSENT;

  return `or:${openRouter}|an:${fingerprintOf(inputs.anthropicKey)}`;
}

/** Split `or:X|an:Y` back into slots. Total: an unknown or malformed slot reads as ABSENT. */
function slots(fingerprint: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of fingerprint.split('|')) {
    const idx = part.indexOf(':');
    if (idx > 0) out[part.slice(0, idx)] = part.slice(idx + 1);
  }
  return out;
}

/**
 * Whether a fingerprint change is a real rotation worth respawning for.
 *
 * ## Why this is not just `recorded !== current` (CodeRabbit, PR #3729)
 *
 * `/host/refresh` cannot distinguish "no credential" from "we failed to produce
 * one": both the openrouter-mode resolution and the direct-Anthropic decrypt are
 * wrapped in `try/catch` blocks that log a warning and fall back to `null`
 * (`packages/api/src/routes/host-runtime.ts`). So a transient DB hiccup or
 * decrypt fault arrives at the manager looking exactly like an operator removing
 * the key.
 *
 * Under a naive compare that would respawn a perfectly healthy serve and drop its
 * conversation state (ENG-7975) — and a FLAPPING backend would do it on every
 * flap, which is the steady silent context loss this feature exists to avoid.
 *
 * The rule, per provider slot:
 *
 *   - `none → hash`   RESTART. The agent gained a credential; a respawn is how it
 *                     starts working.
 *   - `hashA → hashB` RESTART. A genuine rotation — the case ENG-8093 is about.
 *   - `hash → none`   DO NOT RESTART, and do not update the baseline. We cannot
 *                     tell a real removal from a backend fault, and the asymmetry
 *                     is decisive: respawning into keylessness GUARANTEES a broken
 *                     agent, while leaving the serve alone means a still-valid key
 *                     keeps working. Not updating the baseline is what makes a
 *                     flap a no-op — when the credential returns it matches the
 *                     retained value and nothing restarts.
 *
 * The cost is that a deliberate, permanent credential removal no longer forces a
 * respawn. That is the right trade for an ambiguous signal, but the durable fix
 * is server-side: have /host/refresh distinguish "absent" from "errored" so the
 * manager can act on a real removal. Tracked as a follow-up.
 */
export function shouldRestartForCredentialChange(recorded: string, current: string): boolean {
  if (recorded === current) return false;

  const before = slots(recorded);
  const after = slots(current);

  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const was = before[key] ?? ABSENT;
    const now = after[key] ?? ABSENT;
    if (was === now) continue;
    // A credential that vanished is unreliable evidence — ignore this slot.
    if (now === ABSENT) continue;
    return true;
  }
  return false;
}
